(() => {
  if (!window.EnergieSystemAdapter) {
    window.EnergieSystemAdapter = {
      async addSystem(payload) {
        const properties = {
          _energie_system_id: payload.systemId,
          _energie_system_tier: String(payload.tier),
          _energie_system_selection: payload.selection.join(','),
          _energie_system_resolution: payload.winner,
        };
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: payload.lines.map((line) => ({
              id: Number(line.id),
              quantity: line.quantity,
              properties,
            })),
          }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || `Shopify respondió ${response.status}`);
        }
        return response.json();
      },
    };
  }

  const root = document.getElementById('energie-system-builder');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const configNode = root.querySelector('[data-energie-system-config]');
  if (!configNode) return;

  const config = JSON.parse(configNode.textContent);
  const shippingThreshold = Number(config.shippingThreshold || 169900);
  const selected = new Set();
  let hidraFlavor = config.defaultHidraFlavor;

  const money = (cents) => new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

  const productForFamily = (family) => {
    if (family !== 'hidra') return config.products[family];
    return config.products.hidra[hidraFlavor];
  };

  const selectionKeys = () => [...selected].map((family) => {
    if (family !== 'hidra') return family;
    return `hidra_${hidraFlavor}`;
  });

  const selectionKey = () => {
    const order = ['hidra_frutos', 'hidra_limon', 'avant', 'creatina', 'magnuit', 'omega'];
    return selectionKeys().sort((a, b) => order.indexOf(a) - order.indexOf(b)).join('+');
  };

  // Escala normal: el descuento va dentro del producto bundle (snippets/energie-system-bundle-map).
  // Evento: los descuentos automáticos de Shopify cubren solo productos sueltos y packs, así que
  // en esas fechas se agregan los productos sueltos y la escala sale de config.event.
  const BASE_TIERS = { 2: 0.05, 3: 0.08, 4: 0.10 };
  const ev = config.event || null;
  const phaseAt = (time) => {
    if (!ev || time < Date.parse(ev.startReg) || time >= Date.parse(ev.end)) return 'off';
    return time < Date.parse(ev.startAll) ? 'reg' : 'all';
  };
  const currentPhase = () => phaseAt(Date.now());
  // Registrado = sesión iniciada con el tag del evento (Liquid). Sin sesión se cotiza la escala pública.
  const registered = Boolean(ev?.registered);
  const tiersFor = (phase, reg = registered) => {
    if (phase === 'reg' || (phase === 'all' && reg)) return ev.regTiers;
    if (phase === 'all') return ev.publicTiers;
    return BASE_TIERS;
  };
  const rateFor = (count, phase, reg) => Number(tiersFor(phase, reg)[count] || 0);
  const packPrice = (pack, phase = currentPhase()) => (
    phase === 'off' ? pack.price : pack.price - Number(ev.packOff?.[pack.handle] || 0)
  );
  const pct = (rate) => Math.round(rate * 100);

  const subsets = (items) => {
    const result = [];
    for (let mask = 1; mask < 2 ** items.length; mask += 1) {
      result.push(items.filter((_, index) => mask & (1 << index)));
    }
    return result;
  };

  const bestOption = (reg = registered) => {
    const phase = currentPhase();
    const families = [...selected];
    const count = families.length;
    const subtotal = families.reduce((sum, family) => sum + productForFamily(family).price, 0);
    const rate = rateFor(count, phase, reg);
    const candidates = [];

    if (count < 5 || phase !== 'off') {
      candidates.push({
        kind: 'system',
        total: Math.round(subtotal * (1 - rate)),
        packs: [],
        looseFamilies: families,
        label: 'Tu ÉNERGIE System',
      });
    }

    const selectedSet = new Set(families);
    const eligiblePacks = config.packs.filter((pack) => {
      if (!pack.available || !pack.covers.every((family) => selectedSet.has(family))) return false;
      return !pack.flavor || pack.flavor === hidraFlavor;
    });

    for (const chosenPacks of subsets(eligiblePacks)) {
      const covered = new Set();
      let overlaps = false;
      chosenPacks.forEach((pack) => pack.covers.forEach((family) => {
        if (covered.has(family)) overlaps = true;
        covered.add(family);
      }));
      if (overlaps) continue;

      const looseFamilies = families.filter((family) => !covered.has(family));
      const total = chosenPacks.reduce((sum, pack) => sum + packPrice(pack, phase), 0)
        + looseFamilies.reduce((sum, family) => sum + productForFamily(family).price, 0);
      candidates.push({
        kind: 'pack',
        total,
        packs: chosenPacks,
        looseFamilies,
        label: [
          ...chosenPacks.map((pack) => pack.label),
          ...looseFamilies.map((family) => productForFamily(family).shortName),
        ].join(' + '),
      });
    }

    candidates.sort((a, b) => a.total - b.total || Number(b.kind === 'pack') - Number(a.kind === 'pack'));
    const winner = candidates[0] || null;
    return { phase, count, subtotal, rate, winner };
  };

  const setSelected = (families) => {
    selected.clear();
    families.forEach((family) => {
      const product = productForFamily(family);
      if (product?.available) selected.add(family);
    });
  };

  const renderSelectedList = () => {
    const list = root.querySelector('[data-selected-list]');
    list.replaceChildren();
    if (!selected.size) {
      const empty = document.createElement('div');
      empty.className = 'es-empty';
      empty.textContent = 'Selecciona al menos un producto para empezar.';
      list.append(empty);
      return;
    }

    selected.forEach((family) => {
      const product = productForFamily(family);
      const row = document.createElement('div');
      row.className = 'es-selected-item';
      const name = document.createElement('span');
      name.textContent = product.name;
      const price = document.createElement('strong');
      price.textContent = money(product.price);
      row.append(name, price);
      list.append(row);
    });
  };

  const heroTiers = root.querySelector('.es-hero-tiers');
  const policy = root.querySelector('.es-policy');
  const original = {
    heroTiers: heroTiers?.innerHTML,
    policy: policy?.textContent,
    tiers: new Map([...root.querySelectorAll('[data-tier]')].map((tier) => [tier, tier.textContent])),
  };
  const eventNote = document.createElement('p');
  eventNote.className = 'es-hero-copy es-event-note';
  eventNote.hidden = true;
  root.querySelector('.es-hero-copy')?.after(eventNote);

  const REG_SCALE = '15% con 2 productos, 20% con 3, 23% con 4 y 25% con 5 (5% si llevas uno)';
  const EVENT_COPY = {
    registered: `Ya estás registrado: ${REG_SCALE}. Hasta el 17 de noviembre.`,
    reg: `Acceso anticipado para registrados: ${REG_SCALE}. Inicia sesión con el correo de tu registro para que se aplique. El 14 abre para todos.`,
    all: 'Hasta el 17 de noviembre. ¿Te registraste? Inicia sesión con el correo de tu registro: tu escala sube 5% y te armamos la opción más barata para ti.',
  };
  const loginHref = () => `${ev.loginUrl}?return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
  const anchor = (href, text) => {
    const link = document.createElement('a');
    link.href = href;
    link.textContent = text;
    return link;
  };

  const renderEventCopy = (phase) => {
    const on = phase !== 'off';
    const tiers = tiersFor(phase);
    eventNote.hidden = !on;
    if (on) {
      eventNote.replaceChildren();
      const title = document.createElement('strong');
      title.textContent = `${ev.name}. `;
      if (registered) eventNote.append(title, EVENT_COPY.registered);
      else {
        eventNote.append(title, `${EVENT_COPY[phase]} `, anchor(loginHref(), 'Iniciar sesión'), ' · ',
          anchor(ev.registerUrl, '¿Aún no te registras?'));
      }
    }
    if (heroTiers) {
      if (on) {
        heroTiers.replaceChildren(...[2, 3, 4].map((n) => { // 3 como el original; la de 5 va en la nota y el panel
          const span = document.createElement('span');
          const strong = document.createElement('strong');
          strong.textContent = `${pct(tiers[n])}%`;
          span.append(strong, ` ${n} productos`);
          return span;
        }));
      } else if (heroTiers.innerHTML !== original.heroTiers) {
        heroTiers.innerHTML = original.heroTiers;
      }
    }
    original.tiers.forEach((text, tier) => {
      const n = Number(tier.dataset.tier);
      tier.textContent = on ? `${n} productos · ${pct(tiers[n])}%` : text;
    });
    if (policy) {
      policy.textContent = on
        ? 'Durante el evento, Base y High Performance bajan $100 y el Full Stack $200. El descuento se aplica en el checkout y no se acumula con cupones.'
        : original.policy;
    }
  };

  const nextTierText = (phase, count) => {
    const tiers = tiersFor(phase);
    const counts = Object.keys(tiers).map(Number).sort((a, b) => a - b);
    const next = counts.find((n) => n > count);
    if (next) return `Agrega ${next - count} para ${count < counts[0] ? 'desbloquear' : 'llegar a'} ${pct(tiers[next])}%`;
    if (phase !== 'off') return 'Escala máxima activa';
    return count === 4 ? 'Agrega 1 para comparar Full Stack' : 'Full Stack activo';
  };

  let renderedPhase = null;
  let adding = false; // petición al carrito en curso: ningún render vuelve a habilitar el botón
  const render = () => {
    const result = bestOption();
    const eventOn = result.phase !== 'off';
    renderEventCopy(result.phase);
    const total = result.winner?.total || result.subtotal;
    const saving = result.subtotal - total;

    root.querySelectorAll('[data-family]').forEach((card) => {
      const family = card.dataset.family;
      const active = selected.has(family);
      card.dataset.selected = String(active);
      const button = card.querySelector('[data-toggle]');
      button.setAttribute('aria-pressed', String(active));
      if (!button.disabled) button.textContent = active ? 'Quitar' : 'Agregar';
    });

    const hidraProduct = productForFamily('hidra');
    root.querySelector('[data-hidra-image]').src = hidraProduct.image;
    root.querySelector('[data-hidra-price]').textContent = money(hidraProduct.price);
    renderSelectedList();

    root.querySelector('[data-subtotal]').textContent = money(result.subtotal);
    root.querySelector('[data-saving]').textContent = saving > 0 ? `−${money(saving)}` : '—';
    root.querySelector('[data-total]').textContent = money(total);
    root.querySelector('[data-discount-label]').textContent = result.winner?.kind === 'pack'
      ? 'Ahorro con mejor opción'
      : result.rate
        ? `Descuento ${eventOn ? ev.name : 'System'} ${pct(result.rate)}%`
        : 'Ahorro';

    const recommendation = root.querySelector('[data-recommendation]');
    if (result.winner?.kind === 'pack') {
      recommendation.hidden = false;
      root.querySelector('[data-recommendation-title]').textContent = result.winner.label;
      const systemTotal = result.count < 5 || eventOn ? Math.round(result.subtotal * (1 - result.rate)) : null;
      const advantage = systemTotal === null ? result.subtotal - result.winner.total : systemTotal - result.winner.total;
      root.querySelector('[data-recommendation-copy]').textContent = result.count === 5 && !eventOn
        ? `Sustituimos tu selección por el Full Stack y ahorras ${money(advantage)}.`
        : `Esta opción es ${money(advantage)} más barata que armar los productos con el descuento ${eventOn ? ev.name : 'System'}.`;
    } else if (result.count >= 2 || result.rate > 0) {
      recommendation.hidden = false;
      root.querySelector('[data-recommendation-title]').textContent = `System personalizado · ${pct(result.rate)}% OFF`;
      root.querySelector('[data-recommendation-copy]').textContent = 'No existe un pack más barato para esta combinación exacta.';
    } else {
      recommendation.hidden = true;
    }

    // Sin sesión se cotiza la escala pública; si al registrado le conviene otra combinación, se le dice.
    if (result.phase === 'all' && !registered && result.winner) {
      const alt = bestOption(true);
      const altKey = (option) => `${option.kind}:${option.packs.map((pack) => pack.handle).join('+')}`;
      if (alt.winner && altKey(alt.winner) !== altKey(result.winner)) {
        const altLabel = alt.winner.kind === 'pack' ? alt.winner.label : 'productos sueltos';
        recommendation.hidden = false;
        root.querySelector('[data-recommendation-copy]').append(
          ` ¿Te registraste? Inicia sesión y esta selección te queda en ${money(alt.winner.total)} con ${altLabel}. `,
          anchor(loginHref(), 'Iniciar sesión'),
        );
      }
    }
    renderedPhase = result.phase;

    root.querySelectorAll('[data-tier]').forEach((tier) => {
      tier.dataset.active = String(Number(tier.dataset.tier) === result.count);
    });

    root.querySelector('[data-next-tier]').textContent = nextTierText(result.phase, result.count);

    const remaining = Math.max(0, shippingThreshold - total);
    root.querySelector('[data-shipping-copy]').textContent = remaining
      ? `Te faltan ${money(remaining)} para envío gratis`
      : 'Envío gratis desbloqueado';
    root.querySelector('[data-shipping-bar]').style.width = `${Math.min(100, total / shippingThreshold * 100)}%`;

    const checkout = root.querySelector('[data-checkout]');
    checkout.disabled = !result.count || adding;
    checkout.textContent = result.winner?.kind === 'pack'
      ? `Agregar ${result.winner.label}`
      : 'Agregar mi ÉNERGIE System';
    if (!adding) root.querySelector('[data-result]').textContent = '';
  };

  const payloadForCheckout = () => {
    const result = bestOption();
    if (!result.winner) return null;
    const systemId = window.crypto?.randomUUID?.() || `energie-${Date.now()}`;
    const lines = [];

    if (result.winner.kind === 'system' && result.phase !== 'off') {
      result.winner.looseFamilies.forEach((family) => {
        const product = productForFamily(family);
        lines.push({ id: product.variantId, quantity: 1, role: 'product', handle: product.handle });
      });
    } else if (result.winner.kind === 'system') {
      const system = config.systems?.[selectionKey()];
      if (!system) return { error: 'No existe un bundle configurado para esta selección.' };
      lines.push({ id: system.variantId, quantity: 1, role: 'system', handle: system.handle });
    } else {
      result.winner.packs.forEach((pack) => lines.push({
        id: pack.variantId,
        quantity: 1,
        role: 'pack',
        handle: pack.handle,
      }));
      result.winner.looseFamilies.forEach((family) => {
        const product = productForFamily(family);
        lines.push({ id: product.variantId, quantity: 1, role: 'product', handle: product.handle });
      });
    }

    return {
      source: 'arma_tu_energie_system',
      systemId,
      tier: result.count,
      discountPercent: Math.round(result.rate * 100),
      hidraFlavor: selected.has('hidra') ? hidraFlavor : null,
      selection: selectionKeys(),
      resolutionKey: selectionKey(),
      winner: result.winner.kind,
      lines,
    };
  };

  root.querySelectorAll('[data-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const family = button.dataset.toggle;
      if (selected.has(family)) selected.delete(family);
      else if (productForFamily(family)?.available) selected.add(family);
      root.querySelectorAll('[data-preset]').forEach((preset) => preset.setAttribute('aria-pressed', 'false'));
      window.dataLayer?.push({ event: 'energie_system_selection', family, selected: selected.has(family) });
      render();
    });
  });

  root.querySelectorAll('[data-preset]').forEach((button) => {
    button.addEventListener('click', () => {
      setSelected(config.presets[button.dataset.preset] || []);
      root.querySelectorAll('[data-preset]').forEach((preset) => preset.setAttribute('aria-pressed', String(preset === button)));
      window.dataLayer?.push({ event: 'energie_system_preset', preset: button.dataset.preset });
      render();
    });
  });

  root.querySelector('[data-flavor-select]').addEventListener('change', (event) => {
    hidraFlavor = event.target.value;
    render();
  });

  root.querySelector('[data-checkout]').addEventListener('click', async () => {
    if (adding) return;
    const resultNode = root.querySelector('[data-result]');
    if (currentPhase() !== renderedPhase) { // la página quedó abierta al abrir o cerrar el evento
      render();
      resultNode.textContent = 'Los precios cambiaron. Revisa tu total y vuelve a agregar.';
      return;
    }
    const payload = payloadForCheckout();
    if (!payload) return;
    if (payload.error) {
      resultNode.textContent = payload.error;
      return;
    }

    if (root.dataset.poc === 'true') {
      resultNode.textContent = 'POC seguro: selección validada; el carrito no fue modificado.';
      return;
    }

    if (!window.EnergieSystemAdapter?.addSystem) {
      resultNode.textContent = 'El motor de descuentos aún no está conectado. Intenta de nuevo más tarde.';
      return;
    }

    try {
      const checkout = root.querySelector('[data-checkout]');
      adding = true;
      checkout.disabled = true;
      resultNode.textContent = 'Agregando tu System…';
      await window.EnergieSystemAdapter.addSystem(payload);
      window.dataLayer?.push({ event: 'energie_system_add', ...payload });
      window.location.assign('/cart');
    } catch (error) {
      console.error('ENERGIE System:', error);
      adding = false;
      resultNode.textContent = 'No pudimos agregar tu System. Actualiza la página e intenta nuevamente.';
      root.querySelector('[data-checkout]').disabled = false;
    }
  });

  // Regresar del carrito con «atrás» restaura la página desde caché con adding=true.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) { adding = false; render(); }
  });

  setSelected(config.presets.base);
  render();
  if (ev) window.setInterval(() => { if (currentPhase() !== renderedPhase) render(); }, 30000);
  window.dataLayer?.push({ event: 'energie_system_view', mode: root.dataset.poc === 'true' ? 'poc' : 'live' });
})();
